import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./transports.css";

const RU_MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

const fleetInsuranceBadge = (days) => {
    if (days === null || days === undefined) return { cls: "tr-muted", text: "Нет данных" };
    if (days <= 0) return { cls: "tr-bad", text: "Просрочено" };
    if (days <= 60) return { cls: "tr-warn", text: "Истекает скоро" };
    return { cls: "tr-ok", text: "Действует" };
};
const fleetOilBadge = (left) => {
    if (left === null || left === undefined) return { cls: "tr-muted", text: "Нет данных" };
    if (left <= 3000) return { cls: "tr-bad", text: "Пора менять" };
    if (left <= 6000) return { cls: "tr-warn", text: "Скоро" };
    return { cls: "tr-ok", text: "В норме" };
};

// "2026-08-27" -> "2026-08" (ключ для группировки по месяцу)
const getFuelMonthKey = (dateStr) => (dateStr ? String(dateStr).slice(0, 7) : "");
// "2026-08" -> "Август 2026"
const getFuelMonthLabel = (key) => {
    const [y, m] = key.split("-");
    return `${RU_MONTHS[Number(m) - 1] || m} ${y}`;
};

const emptyVpForm = {
    vin: "", techPassport: "", year: "", company: "",
    responsibleManual: "", phone: "", joined: "", mileage: "",
    insuranceCar: null, insurancePax: null, oil: null,
};

const emptyAddFuelForm = { date: new Date().toISOString().slice(0, 10), carId: "", liters: "120", price: "11000", driver: "" };

const Transports = ({ API_URL }) => {
    const [activeTab, setActiveTab] = useState("vehicles"); // 'vehicles' | 'fuel'
    const [search, setSearch] = useState("");

    const [vehicles, setVehicles] = useState([]);
    const [loadingVehicles, setLoadingVehicles] = useState(true);
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);
    const [vpTab, setVpTab] = useState("main"); // 'main' | 'history'
    const [vpForm, setVpForm] = useState(emptyVpForm);

    const [showServiceForm, setShowServiceForm] = useState(false);
    const [serviceForm, setServiceForm] = useState({ date: "", type: "", desc: "", partId: "", partQty: "1", cost: "", exec: "" });

    const [inventoryParts, setInventoryParts] = useState([]);

    const [fuelRecords, setFuelRecords] = useState([]);
    const [loadingFuel, setLoadingFuel] = useState(true);
    const [tours, setTours] = useState([]);
    const [fuelMonthFilter, setFuelMonthFilter] = useState("Все");
    const [fuelVehicleFilter, setFuelVehicleFilter] = useState("Все");
    const [showAddFuelModal, setShowAddFuelModal] = useState(false);
    const [addFuelForm, setAddFuelForm] = useState(emptyAddFuelForm);

    const fetchVehicles = useCallback(() => {
        setLoadingVehicles(true);
        axios.post(`${API_URL}/getVehicles`)
            .then((res) => { if (res.data.status === "success") setVehicles(res.data.vehicles); })
            .catch(() => toast.error("Не удалось загрузить автопарк"))
            .finally(() => setLoadingVehicles(false));
    }, [API_URL]);

    const fetchInventoryParts = useCallback(() => {
        axios.post(`${API_URL}/getInventoryItems`)
            .then((res) => { if (res.data.status === "success") setInventoryParts(res.data.items.filter((i) => i.category === "Запчасти")); })
            .catch(() => { });
    }, [API_URL]);

    const fetchFuel = useCallback(() => {
        setLoadingFuel(true);
        axios.post(`${API_URL}/getFuelLog`)
            .then((res) => { if (res.data.status === "success") setFuelRecords(res.data.records); })
            .catch(() => toast.error("Не удалось загрузить журнал заправок"))
            .finally(() => setLoadingFuel(false));
    }, [API_URL]);

    const fetchTours = useCallback(() => {
        axios.post(`${API_URL}/getToursForFuelSelect`)
            .then((res) => { if (res.data.status === "success") setTours(res.data.tours); })
            .catch(() => { });
    }, [API_URL]);

    useEffect(() => { fetchVehicles(); fetchInventoryParts(); fetchFuel(); fetchTours(); }, [fetchVehicles, fetchInventoryParts, fetchFuel, fetchTours]);

    useEffect(() => {
        if (!selectedVehicleId) { setVpForm(emptyVpForm); return; }
        const v = vehicles.find((x) => x.id === selectedVehicleId);
        if (!v) return;
        setVpForm({
            vin: v.vin || "", techPassport: v.techPassport || "",
            year: v.year || "", company: v.company || "",
            responsibleManual: v.responsibleSource === "manual" ? (v.responsible || "") : "",
            phone: v.phone || "", joined: v.joined || "", mileage: v.mileage ?? "",
            insuranceCar: v.insuranceCar ? { ...v.insuranceCar } : null,
            insurancePax: v.insurancePax ? { ...v.insurancePax } : null,
            oil: v.oil ? { ...v.oil } : null,
        });
    }, [selectedVehicleId, vehicles]);

    const selectedVehicle = vehicles.find((x) => x.id === selectedVehicleId) || null;

    const q = search.trim().toLowerCase();
    const visibleVehicles = useMemo(() => vehicles.filter((v) =>
        !q || v.name.toLowerCase().includes(q) || (v.responsible || "").toLowerCase().includes(q)
    ), [vehicles, q]);

    const openVehicle = (id) => { setSelectedVehicleId(id); setVpTab("main"); setShowServiceForm(false); };
    const backToList = () => { setSelectedVehicleId(null); };

    const saveVehicle = () => {
        axios.post(`${API_URL}/updateVehicle`, { id: selectedVehicleId, ...vpForm })
            .then((res) => { if (res.data.status === "success") { toast.success("Изменения сохранены"); fetchVehicles(); } else toast.error("Ошибка"); })
            .catch(() => toast.error("Ошибка сервера"));
    };

    const addInsuranceCar = () => axios.post(`${API_URL}/addVehicleInsuranceCar`, { id: selectedVehicleId }).then(() => fetchVehicles()).catch(() => toast.error("Ошибка сервера"));
    const addInsurancePax = () => axios.post(`${API_URL}/addVehicleInsurancePax`, { id: selectedVehicleId }).then(() => fetchVehicles()).catch(() => toast.error("Ошибка сервера"));
    const addOilData = () => axios.post(`${API_URL}/addVehicleOilData`, { id: selectedVehicleId }).then(() => fetchVehicles()).catch(() => toast.error("Ошибка сервера"));

    const submitServiceEntry = () => {
        if (!serviceForm.type.trim()) { toast.error("Укажите вид работ"); return; }
        axios.post(`${API_URL}/addVehicleServiceEntry`, {
            vehicleId: selectedVehicleId,
            date: serviceForm.date || new Date().toISOString().slice(0, 10),
            type: serviceForm.type, desc: serviceForm.desc, cost: Number(serviceForm.cost) || 0, exec: serviceForm.exec,
            partId: serviceForm.partId || null, partQty: Number(serviceForm.partQty) || 1,
        }).then((res) => {
            if (res.data.status === "success") {
                toast.success("Запись добавлена");
                setShowServiceForm(false);
                setServiceForm({ date: "", type: "", desc: "", partId: "", partQty: "1", cost: "", exec: "" });
                fetchVehicles(); fetchInventoryParts();
            } else toast.error(res.data.message || "Ошибка");
        }).catch(() => toast.error("Ошибка сервера"));
    };

    const fuelMonthKeys = Array.from(new Set(fuelRecords.map((f) => getFuelMonthKey(f.date)))).sort().reverse();
    const fuelVehicles = Array.from(new Set(fuelRecords.map((f) => f.plate))).sort();
    const filteredFuel = fuelRecords.filter((f) =>
        (fuelMonthFilter === "Все" || getFuelMonthKey(f.date) === fuelMonthFilter) &&
        (fuelVehicleFilter === "Все" || f.plate === fuelVehicleFilter) &&
        (!q || f.plate.toLowerCase().includes(q) || (f.driver || "").toLowerCase().includes(q))
    );

    // машина выбрана в модалке добавления заправки → авто-подставляем ответственного, если он закреплён
    const selectedFuelVehicle = vehicles.find((v) => v.id === Number(addFuelForm.carId)) || null;
    const fuelDriverIsAuto = selectedFuelVehicle?.responsibleSource === "driver";

    const openAddFuelModal = () => { setAddFuelForm(emptyAddFuelForm); setShowAddFuelModal(true); };

    const onFuelCarChange = (carId) => {
        const v = vehicles.find((x) => x.id === Number(carId));
        setAddFuelForm({
            ...addFuelForm,
            carId,
            driver: v?.responsibleSource === "driver" ? v.responsible : "",
        });
    };

    const submitAddFuel = () => {
        if (!addFuelForm.carId) { toast.error("Выберите машину"); return; }
        if (!addFuelForm.date) { toast.error("Укажите дату"); return; }
        if (!fuelDriverIsAuto && !addFuelForm.driver.trim()) { toast.error("Укажите водителя"); return; }
        axios.post(`${API_URL}/addFuelEntry`, addFuelForm)
            .then((res) => {
                if (res.data.status === "success") {
                    toast.success("Заправка добавлена");
                    setShowAddFuelModal(false);
                    fetchFuel();
                } else toast.error(res.data.message || "Ошибка");
            }).catch(() => toast.error("Ошибка сервера"));
    };

    const updateFuelLiters = (id, value) => {
        setFuelRecords((prev) => prev.map((f) => f.id === id ? { ...f, liters: Number(value) || 0 } : f));
        axios.post(`${API_URL}/updateFuelField`, { id, field: "liters", value }).catch(() => toast.error("Не удалось сохранить"));
    };

    const updateFuelTourLink = (id, combined) => {
        const [requestType, orderNumber] = combined ? combined.split(":") : [null, null];
        setFuelRecords((prev) => prev.map((f) => f.id === id ? { ...f, requestType, orderNumber } : f));
        axios.post(`${API_URL}/updateFuelTour`, { id, requestType: requestType || null, orderNumber: orderNumber || null })
            .catch(() => toast.error("Не удалось сохранить привязку"));
    };
    const deleteFuelEntry = (id) => {
        if (!window.confirm("Удалить эту заправку?")) return;
        axios.post(`${API_URL}/deleteFuelEntry`, { id })
            .then((res) => {
                if (res.data.status === "success") { toast.success("Заправка удалена"); fetchFuel(); }
                else toast.error(res.data.message || "Ошибка");
            }).catch(() => toast.error("Ошибка сервера"));
    };
    const requestTypeLabel = { individual: "Индивид.", sharetour: "Share Tour", partners: "Партнёр" };

    // туры не раньше даты самой заправки — заправка от 27.08 не может быть потрачена на тур от 26.08
    const toursForRecord = (fuelDate) => tours.filter((t) => !fuelDate || !t.dateStart || t.dateStart >= fuelDate);

    /* ==================================================================== */
    /* ЭКРАН: КАРТОЧКА МАШИНЫ                                                */
    /* ==================================================================== */
    if (selectedVehicleId && selectedVehicle) {
        const insBadge = fleetInsuranceBadge(selectedVehicle.insuranceCar?.daysLeft);
        const paxBadge = fleetInsuranceBadge(selectedVehicle.insurancePax?.daysLeft);
        const oilBadge = fleetOilBadge(selectedVehicle.oil?.left);
        const hasAutoResponsible = selectedVehicle.responsibleSource === "driver";

        return (
            <div className="tr-page">
                <button className="tr-back" onClick={backToList}><i className="fa-solid fa-arrow-left" /> Все машины</button>
                <div className="tr-detail-top">
                    <div className="tr-detail-title">{selectedVehicle.name}</div>
                    <button className="tr-btn-primary" onClick={saveVehicle}><i className="fa-solid fa-floppy-disk" /> Сохранить</button>
                </div>

                <div className="tr-tabs">
                    <div className={"tr-tab" + (vpTab === "main" ? " tr-tab-active" : "")} onClick={() => setVpTab("main")}>Основное</div>
                    <div className={"tr-tab" + (vpTab === "history" ? " tr-tab-active" : "")} onClick={() => setVpTab("history")}>История ТО</div>
                </div>

                {vpTab === "main" && (
                    <>
                        <div className="tr-section">
                            <div className="tr-section-head"><i className="fa-solid fa-circle-info" /> Основная информация</div>
                            <div className="tr-section-body">
                                <div className="tr-hint">
                                    Машина «{selectedVehicle.name}» ведётся в общем справочнике машин — переименование и удаление там же.
                                </div>
                                <div className="tr-fgrid">
                                    <div className="tr-field"><label>VIN номер</label><input value={vpForm.vin} onChange={(e) => setVpForm({ ...vpForm, vin: e.target.value })} /></div>
                                    <div className="tr-field"><label>№ техпаспорта</label><input value={vpForm.techPassport} onChange={(e) => setVpForm({ ...vpForm, techPassport: e.target.value })} /></div>
                                    <div className="tr-field"><label>Год выпуска</label><input value={vpForm.year} onChange={(e) => setVpForm({ ...vpForm, year: e.target.value })} /></div>
                                    <div className="tr-field"><label>Компания-владелец</label><input value={vpForm.company} onChange={(e) => setVpForm({ ...vpForm, company: e.target.value })} /></div>

                                    <div className="tr-field">
                                        <label>Ответственный</label>
                                        {hasAutoResponsible ? (
                                            <div className="tr-readonly-value">
                                                {selectedVehicle.responsible}
                                                <span className="tr-badge-status tr-ok" style={{ marginLeft: 8 }}>закреплён как водитель 4x4</span>
                                            </div>
                                        ) : (
                                            <input
                                                value={vpForm.responsibleManual}
                                                onChange={(e) => setVpForm({ ...vpForm, responsibleManual: e.target.value })}
                                                placeholder="Например: Тазабай ага"
                                            />
                                        )}
                                    </div>

                                    <div className="tr-field"><label>Телефон ответственного</label><input value={vpForm.phone} onChange={(e) => setVpForm({ ...vpForm, phone: e.target.value })} /></div>
                                    <div className="tr-field"><label>Дата постановки в автопарк</label><input value={vpForm.joined} onChange={(e) => setVpForm({ ...vpForm, joined: e.target.value })} /></div>
                                    <div className="tr-field"><label>Текущий пробег, км</label><input value={vpForm.mileage} onChange={(e) => setVpForm({ ...vpForm, mileage: e.target.value })} /></div>
                                </div>
                            </div>
                        </div>

                        <div className="tr-section">
                            <div className="tr-section-head">
                                <i className="fa-solid fa-shield-halved" /> Страхование автомобиля
                                <span className={"tr-badge-status " + insBadge.cls}>{insBadge.text}</span>
                            </div>
                            <div className="tr-section-body">
                                {vpForm.insuranceCar ? (
                                    <div className="tr-fgrid">
                                        <div className="tr-field"><label>Страховая компания</label><input value={vpForm.insuranceCar.company || ""} onChange={(e) => setVpForm({ ...vpForm, insuranceCar: { ...vpForm.insuranceCar, company: e.target.value } })} /></div>
                                        <div className="tr-field"><label>Номер полиса</label><input value={vpForm.insuranceCar.policy || ""} onChange={(e) => setVpForm({ ...vpForm, insuranceCar: { ...vpForm.insuranceCar, policy: e.target.value } })} /></div>
                                        <div className="tr-field"><label>Дата оформления</label><input value={vpForm.insuranceCar.issued || ""} onChange={(e) => setVpForm({ ...vpForm, insuranceCar: { ...vpForm.insuranceCar, issued: e.target.value } })} /></div>
                                        <div className="tr-field"><label>Дата окончания</label><input value={vpForm.insuranceCar.expires || ""} onChange={(e) => setVpForm({ ...vpForm, insuranceCar: { ...vpForm.insuranceCar, expires: e.target.value } })} /></div>
                                    </div>
                                ) : <span className="tr-addrow" onClick={addInsuranceCar}><i className="fa-solid fa-plus" /> Добавить данные страховки авто</span>}
                            </div>
                        </div>

                        <div className="tr-section">
                            <div className="tr-section-head">
                                <i className="fa-solid fa-user-shield" /> Страхование пассажиров
                                {vpForm.insurancePax && <span className={"tr-badge-status " + paxBadge.cls}>{paxBadge.text}</span>}
                            </div>
                            <div className="tr-section-body">
                                {vpForm.insurancePax ? (
                                    <div className="tr-fgrid-two">
                                        <div className="tr-field"><label>Страховая компания</label><input value={vpForm.insurancePax.company || ""} onChange={(e) => setVpForm({ ...vpForm, insurancePax: { ...vpForm.insurancePax, company: e.target.value } })} /></div>
                                        <div className="tr-field"><label>Дата окончания</label><input value={vpForm.insurancePax.expires || ""} onChange={(e) => setVpForm({ ...vpForm, insurancePax: { ...vpForm.insurancePax, expires: e.target.value } })} /></div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="tr-hint">Полис пассажирского страхования пока не оформлен.</div>
                                        <span className="tr-addrow" onClick={addInsurancePax}><i className="fa-solid fa-plus" /> Добавить данные страховки пассажиров</span>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="tr-section">
                            <div className="tr-section-head">
                                <i className="fa-solid fa-oil-can" /> Замена масла
                                {vpForm.oil && <span className={"tr-badge-status " + oilBadge.cls}>{oilBadge.text}</span>}
                            </div>
                            <div className="tr-section-body">
                                {vpForm.oil ? (
                                    <div className="tr-fgrid">
                                        <div className="tr-field"><label>Дата последней замены</label><input value={vpForm.oil.lastDate || ""} onChange={(e) => setVpForm({ ...vpForm, oil: { ...vpForm.oil, lastDate: e.target.value } })} /></div>
                                        <div className="tr-field"><label>Пробег на момент замены, км</label><input value={vpForm.oil.lastMileage ?? ""} onChange={(e) => setVpForm({ ...vpForm, oil: { ...vpForm.oil, lastMileage: e.target.value } })} /></div>
                                        <div className="tr-field"><label>Интервал замены, км</label><input value={vpForm.oil.interval ?? ""} onChange={(e) => setVpForm({ ...vpForm, oil: { ...vpForm.oil, interval: e.target.value } })} /></div>
                                        <div className="tr-field"><label>Пробег для след. замены, км</label><input value={vpForm.oil.nextMileage ?? ""} onChange={(e) => setVpForm({ ...vpForm, oil: { ...vpForm.oil, nextMileage: e.target.value } })} /></div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="tr-hint">Данные о замене масла не заполнены.</div>
                                        <span className="tr-addrow" onClick={addOilData}><i className="fa-solid fa-plus" /> Добавить данные о замене масла</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {vpTab === "history" && (
                    <div className="tr-section">
                        <div className="tr-section-head"><i className="fa-solid fa-screwdriver-wrench" /> История обслуживания и ремонта</div>
                        <div className="tr-section-body">
                            <div className="tr-table-wrap">
                                <table className="tr-table">
                                    <thead>
                                        <tr><th>Дата</th><th>Пробег</th><th>Вид работ</th><th>Описание</th><th>Запчасти</th><th>Стоимость</th><th>Исполнитель</th></tr>
                                    </thead>
                                    <tbody>
                                        {selectedVehicle.history.length ? selectedVehicle.history.map((h) => (
                                            <tr key={h.id}>
                                                <td>{h.date}</td><td className="tr-mono">{h.mileage}</td><td>{h.type}</td>
                                                <td>{h.desc}</td><td>{h.parts}</td><td className="tr-mono">{h.cost}</td><td>{h.exec}</td>
                                            </tr>
                                        )) : <tr><td colSpan={7} className="tr-hint-cell">Записей пока нет.</td></tr>}
                                    </tbody>
                                </table>
                            </div>

                            {!showServiceForm ? (
                                <span className="tr-addrow" onClick={() => setShowServiceForm(true)}><i className="fa-solid fa-plus" /> Добавить запись ТО</span>
                            ) : (
                                <div className="tr-mini-form">
                                    <div className="tr-fgrid-two">
                                        <div className="tr-field"><label>Дата</label><input type="date" value={serviceForm.date} onChange={(e) => setServiceForm({ ...serviceForm, date: e.target.value })} /></div>
                                        <div className="tr-field"><label>Вид работ</label><input value={serviceForm.type} onChange={(e) => setServiceForm({ ...serviceForm, type: e.target.value })} placeholder="Замена масла, ремонт..." /></div>
                                    </div>
                                    <div className="tr-field"><label>Описание</label><input value={serviceForm.desc} onChange={(e) => setServiceForm({ ...serviceForm, desc: e.target.value })} placeholder="Что было сделано" /></div>
                                    <div className="tr-fgrid-two">
                                        <div className="tr-field">
                                            <label>Запчасть со склада</label>
                                            <select value={serviceForm.partId} onChange={(e) => setServiceForm({ ...serviceForm, partId: e.target.value })}>
                                                <option value="">Не использовалась</option>
                                                {inventoryParts.map((p) => <option key={p.id} value={p.id}>{p.name} (остаток {p.qty} {p.unit})</option>)}
                                            </select>
                                        </div>
                                        <div className="tr-field"><label>Количество</label><input value={serviceForm.partQty} onChange={(e) => setServiceForm({ ...serviceForm, partQty: e.target.value })} /></div>
                                    </div>
                                    <div className="tr-fgrid-two">
                                        <div className="tr-field"><label>Стоимость работ, сум</label><input value={serviceForm.cost} onChange={(e) => setServiceForm({ ...serviceForm, cost: e.target.value })} placeholder="0" /></div>
                                        <div className="tr-field"><label>Исполнитель / СТО</label><input value={serviceForm.exec} onChange={(e) => setServiceForm({ ...serviceForm, exec: e.target.value })} placeholder='Например: СТО «Мотор+»' /></div>
                                    </div>
                                    <button className="tr-btn-primary tr-full" onClick={submitServiceEntry}>Добавить запись</button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    /* ==================================================================== */
    /* ЭКРАН: СПИСОК / ЗАПРАВКА                                              */
    /* ==================================================================== */
    return (
        <div className="tr-page">
            <div className="tr-topbar">
                <div className="tr-search">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input placeholder="Поиск по машине, ответственному" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="tr-pagetitle">Автопарк</div>
            </div>

            <div className="tr-tabs">
                <div className={"tr-tab" + (activeTab === "vehicles" ? " tr-tab-active" : "")} onClick={() => setActiveTab("vehicles")}>Машины</div>
                <div className={"tr-tab" + (activeTab === "fuel" ? " tr-tab-active" : "")} onClick={() => setActiveTab("fuel")}>Заправка</div>
            </div>

            {activeTab === "vehicles" && (
                <>
                    <div className="tr-stats">
                        <div className="tr-stat"><div className="tr-lbl">Машин в парке</div><div className="tr-val">{vehicles.length}</div></div>
                        <div className="tr-stat tr-stat-debt"><div className="tr-lbl">Страховка истекает &lt;60 дней</div><div className="tr-val">{vehicles.filter((v) => v.insuranceCar && v.insuranceCar.daysLeft <= 60).length}</div></div>
                        <div className="tr-stat tr-stat-debt"><div className="tr-lbl">Пора менять масло</div><div className="tr-val">{vehicles.filter((v) => v.oil && v.oil.left <= 6000).length}</div></div>
                    </div>

                    {loadingVehicles ? (
                        <div className="tr-empty">Загрузка…</div>
                    ) : !visibleVehicles.length ? (
                        <div className="tr-empty">Ничего не найдено.</div>
                    ) : (
                        visibleVehicles.map((v) => {
                            const insBadge = fleetInsuranceBadge(v.insuranceCar?.daysLeft);
                            const paxBadge = fleetInsuranceBadge(v.insurancePax?.daysLeft);
                            const oilBadge = fleetOilBadge(v.oil?.left);
                            return (
                                <div className="tr-vitem" key={v.id} onClick={() => openVehicle(v.id)}>
                                    <div className="tr-vitem-row">
                                        <div className="tr-badge"><i className="fa-solid fa-car" /></div>
                                        <div className="tr-vitem-info">
                                            <div className="tr-vitem-name">{v.name}</div>
                                            <div className="tr-vitem-sub">
                                                {v.company || "—"} · {v.responsible ? v.responsible : "без ответственного"}
                                                {v.responsibleSource === "driver" && <span className="tr-auto-tag"></span>}
                                            </div>
                                        </div>
                                        <div className="tr-status-stack">
                                            <div className="tr-status-item">
                                                <span className={"tr-badge-status " + insBadge.cls}>Страховка авто: {insBadge.text}</span>
                                                {v.insuranceCar?.expires && (
                                                    <span className="tr-status-detail">
                                                        до {v.insuranceCar.expires}
                                                        {v.insuranceCar.daysLeft != null && ` · ${v.insuranceCar.daysLeft} дн.`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="tr-status-stack">
                                            <div className="tr-status-item">
                                                <span className={"tr-badge-status " + paxBadge.cls}>Страховка пасс: {paxBadge.text}</span>
                                                {v.insurancePax?.expires && (
                                                    <span className="tr-status-detail">
                                                        до {v.insurancePax.expires}
                                                        {v.insurancePax.daysLeft != null && ` · ${v.insurancePax.daysLeft} дн.`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="tr-status-stack">
                                            <div className="tr-status-item">
                                                <span className={"tr-badge-status " + oilBadge.cls}>Масло: {oilBadge.text}</span>
                                                {v.oil && (
                                                    <span className="tr-status-detail">осталось {v.oil.left} км</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="tr-vitem-metric">
                                            <div className="tr-m-lbl">Пробег</div>
                                            <div className="tr-m-val">{v.mileage} км</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </>
            )}

            {activeTab === "fuel" && (
                <>
                    <div className="tr-fgrid-two">
                        <div className="tr-field"><label>Месяц</label>
                            <select value={fuelMonthFilter} onChange={(e) => setFuelMonthFilter(e.target.value)}>
                                <option>Все</option>
                                {fuelMonthKeys.map((k) => <option key={k} value={k}>{getFuelMonthLabel(k)}</option>)}
                            </select>
                        </div>
                        <div className="tr-field"><label>Машина</label>
                            <select value={fuelVehicleFilter} onChange={(e) => setFuelVehicleFilter(e.target.value)}>
                                <option>Все</option>{fuelVehicles.map((p) => <option key={p}>{p}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="tr-stats">
                        <div className="tr-stat"><div className="tr-lbl">Заправок</div><div className="tr-val">{filteredFuel.length}</div></div>
                        <div className="tr-stat"><div className="tr-lbl">Литров всего</div><div className="tr-val">{filteredFuel.reduce((s, f) => s + f.liters, 0)}</div></div>
                        <div className="tr-stat tr-stat-debt"><div className="tr-lbl">Сумма всего</div><div className="tr-val">{filteredFuel.reduce((s, f) => s + f.liters * f.price, 0)}</div></div>
                        <div className="tr-stat tr-stat-add" onClick={openAddFuelModal}>
                            <i className="fa-solid fa-plus" />
                            <span>Добавить заправку</span>
                        </div>
                    </div>

                    {loadingFuel ? <div className="tr-empty">Загрузка…</div> : (
                        <div className="tr-table-wrap">
                            <table className="tr-table">
                                <thead>
                                    <tr><th>№</th><th>Дата</th><th>Машина</th><th>Объём, л</th><th>Цена/л</th><th>Сумма</th><th>Водитель</th><th>Потрачено на тур</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {filteredFuel.length ? filteredFuel.map((f, idx) => (
                                        <tr key={f.id}>
                                            <td className="tr-mono">{idx + 1}</td>
                                            <td>{f.date}</td>
                                            <td>{f.plate}</td>
                                            <td><input className="tr-cell-input tr-cell-narrow" defaultValue={f.liters} onBlur={(e) => updateFuelLiters(f.id, e.target.value)} /></td>
                                            <td className="tr-mono">{f.price}</td>
                                            <td className="tr-mono">{f.liters * f.price}</td>
                                            <td>{f.driver}</td>
                                            <td>
                                                <select
                                                    defaultValue={f.requestType && f.orderNumber ? `${f.requestType}:${f.orderNumber}` : ""}
                                                    onChange={(e) => updateFuelTourLink(f.id, e.target.value)}
                                                >
                                                    <option value="">Не привязан</option>
                                                    {toursForRecord(f.date).map((t) => (
                                                        <option key={`${t.requestType}-${t.orderNumber}`} value={`${t.requestType}:${t.orderNumber}`}>
                                                            №{t.orderNumber} — {t.route || "Без названия"} ({requestTypeLabel[t.requestType]}, {t.dateStart})
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <button className="tr-btn-delete" onClick={() => deleteFuelEntry(f.id)} title="Удалить заправку">
                                                    <i className="fa-solid fa-trash" />
                                                </button>
                                            </td>
                                        </tr>
                                    )) : <tr><td colSpan={9} className="tr-hint-cell">Нет заправок по выбранным фильтрам.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {showAddFuelModal && (
                <div className="tr-modal-overlay" onClick={() => setShowAddFuelModal(false)}>
                    <div className="tr-modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="tr-modal-head">
                            <div className="tr-modal-title">Новая заправка</div>
                            <button className="tr-modal-close" onClick={() => setShowAddFuelModal(false)}><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="tr-modal-body">
                            <div className="tr-field">
                                <label>Дата</label>
                                <input type="date" value={addFuelForm.date} onChange={(e) => setAddFuelForm({ ...addFuelForm, date: e.target.value })} />
                            </div>
                            <div className="tr-field">
                                <label>Машина</label>
                                <select value={addFuelForm.carId} onChange={(e) => onFuelCarChange(e.target.value)}>
                                    <option value="">— Выберите машину —</option>
                                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                                </select>
                            </div>
                            <div className="tr-fgrid-two">
                                <div className="tr-field"><label>Объём, литр</label><input value={addFuelForm.liters} onChange={(e) => setAddFuelForm({ ...addFuelForm, liters: e.target.value })} /></div>
                                <div className="tr-field"><label>Цена за литр</label><input value={addFuelForm.price} onChange={(e) => setAddFuelForm({ ...addFuelForm, price: e.target.value })} /></div>
                            </div>
                            <div className="tr-field">
                                <label>Водитель</label>
                                {fuelDriverIsAuto ? (
                                    <div className="tr-readonly-value">
                                        {addFuelForm.driver}
                                        <span className="tr-badge-status tr-ok" style={{ marginLeft: 8 }}>закреплён за машиной</span>
                                    </div>
                                ) : (
                                    <input
                                        value={addFuelForm.driver}
                                        onChange={(e) => setAddFuelForm({ ...addFuelForm, driver: e.target.value })}
                                        placeholder="Имя водителя"
                                    />
                                )}
                            </div>
                            <button className="tr-btn-primary tr-full" onClick={submitAddFuel}>Добавить заправку</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Transports;